import { forwardRef, type InputHTMLAttributes, type ReactNode, useId } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix' | 'size'> {
  label?: string;
  hint?: string;
  error?: string;
  /** Podświetla ramkę jak `error`, ale bez renderowania tekstu — do sygnałów serwerowych, gdy treść komunikatu żyje gdzie indziej (np. w toaście). */
  invalid?: boolean;
  /** Stan sukcesu (zielona ramka + ikona) — walidacja potwierdzona, bez tekstu (analogicznie do `invalid`). */
  success?: boolean;
  /**
   * `true` = bez ikony statusu (błąd/sukces) w polu — zostaje sama kolorowa ramka. Dla wąskich pól
   * w gęstych tabelach, gdzie ikona (z odstępem ~40 px) zabrałaby miejsce na wartość. Własna
   * `suffixIcon` konsumenta dalej się wyświetla.
   */
  hideStatusIcon?: boolean;
  /** Sm = 32px, Md = 40px (domyślnie). Zgodnie z macierzą inputów w systemie (Figma „08.2 · Inputs & forms"). */
  size?: 'sm' | 'md';
  prefixIcon?: ReactNode;
  suffixIcon?: ReactNode;
  containerClassName?: string;
}

const SIZE_CLASSES: Record<'sm' | 'md', string> = {
  sm: 'h-8 px-2 text-[13px] leading-[20px]',
  md: 'h-10 px-3 text-[14px] leading-[22px]',
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    hint,
    error,
    invalid,
    success,
    hideStatusIcon = false,
    size = 'md',
    prefixIcon,
    suffixIcon,
    id,
    className,
    containerClassName,
    required,
    'aria-describedby': describedByProp,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [describedByProp, errorId, hintId].filter(Boolean).join(' ') || undefined;

  const isError = Boolean(error || invalid);
  const isSuccess = !isError && Boolean(success);
  // Ikona statusu (błąd/sukces) ma pierwszeństwo przed własną ikoną konsumenta — Figma
  // traktuje ją jako osobny slot „status icon", niezależny od `suffixIcon` (np. toggle hasła).
  const statusIcon = hideStatusIcon ? null : isError ? (
    <AlertCircle className="h-5 w-5 text-[rgb(var(--color-error))]" aria-hidden="true" />
  ) : isSuccess ? (
    <CheckCircle2 className="h-5 w-5 text-[rgb(var(--color-success))]" aria-hidden="true" />
  ) : null;
  const trailingIcon = statusIcon ?? suffixIcon;

  return (
    <div className={['flex flex-col gap-1', containerClassName ?? ''].join(' ')}>
      {label && (
        <label htmlFor={inputId} className="text-sm font-medium text-text-secondary">
          {label}
          {required && <span className="ml-0.5 text-red-600">*</span>}
        </label>
      )}
      <div className="relative">
        {prefixIcon && (
          <span
            className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-text-muted"
            aria-hidden="true"
          >
            {prefixIcon}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          required={required}
          aria-invalid={isError ? true : undefined}
          aria-describedby={describedBy}
          className={[
            'w-full rounded-lg border bg-bg-primary text-text-primary placeholder:text-text-muted',
            'transition-colors',
            SIZE_CLASSES[size],
            'focus:outline-none focus:ring-[3px]',
            'read-only:cursor-default read-only:border-transparent read-only:bg-bg-tertiary',
            'disabled:cursor-not-allowed disabled:border-transparent disabled:bg-bg-secondary disabled:text-text-muted',
            prefixIcon ? 'pl-10' : '',
            trailingIcon ? 'pr-10' : '',
            isError
              ? 'border-[rgb(var(--color-error))] focus:border-[rgb(var(--color-error))] focus:ring-[rgb(var(--color-error))]'
              : isSuccess
                ? 'border-[rgb(var(--color-success))] focus:border-[rgb(var(--color-success))] focus:ring-[rgb(var(--color-success))]'
                : 'border-border-secondary hover:border-border-primary focus:border-accent-600 focus:ring-accent-600',
            className ?? '',
          ].join(' ')}
          {...rest}
        />
        {trailingIcon && (
          <span
            className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
            aria-hidden="true"
          >
            {trailingIcon}
          </span>
        )}
      </div>
      {hint && !error && (
        <span id={hintId} className="text-xs text-text-muted">
          {hint}
        </span>
      )}
      {error && (
        <span id={errorId} className="text-xs text-red-600" role="alert">
          {error}
        </span>
      )}
    </div>
  );
});
